import { z } from 'zod';
/**
 * Curated agent memory — durable, human/agent-authored guidance bound to a set
 * of agents. Lives in the main Cortex DB (unified store, DEV-16805) and is
 * consumed by every agent runtime via the shared resolver + injector. Scoping is
 * a many-to-many BINDING (memory ↔ agent) plus an explicit opt-in "all agents in
 * the org" tier — an agent reads ONLY what is bound to it (the isolation
 * invariant), inside the outer org boundary.
 */
// ─── Enums ──────────────────────────────────────────────────────────────────
/**
 * What kind of thing a memory is. The first four are the original curated-
 * guidance set; most of the rest arrived with the memory graph (DEV-17210) so
 * an agent accumulating its own episodic memory can say what it accumulated
 * instead of filing everything under CONTEXT.
 *
 * PRINCIPLE is judge-stamped, never agent-proposed: the persistent-agent host's
 * principle judge (apps/persistent-agent-host/src/principle-judge.ts) promotes a
 * qualifying proposal and records its verdict in `judgeVerdict`. Humans may also
 * author one directly from the console.
 *
 * Type is also the coarse importance prior — see `memoryTypeBaseImportance` in
 * `@cortex/database`. This list MUST stay in step with the `MemoryType` enum in
 * `schema.prisma`.
 */
export const memoryTypeValues = [
    'RULE',
    'PREFERENCE',
    'CONTEXT',
    'NOTE',
    'PRINCIPLE',
    'IDENTITY',
    'GOAL',
    'DECISION',
    'TODO',
    'FACT',
    'EVENT',
    'OBSERVATION',
];
export const MemoryTypeSchema = z.enum(memoryTypeValues);
/**
 * Types an agent may never self-declare (DEV-17256 trust boundary): PRINCIPLE
 * is judge-stamped, NOTE is console-authored, IDENTITY/GOAL come from agent
 * config. The persistent-agent host already omits these from its save_memory
 * enum; this list is the SERVER-SIDE backstop so the same rule holds on every
 * write path (the REST/MCP surface included) — a create with one of these
 * types requires a memory-governor principal. Keep it in step with the host's
 * `agentWritableMemoryTypes` (the two are complements over `memoryTypeValues`).
 */
export const governanceOnlyMemoryTypes = [
    'PRINCIPLE',
    'NOTE',
    'IDENTITY',
    'GOAL',
];
export function isGovernanceOnlyMemoryType(type) {
    return governanceOnlyMemoryTypes.includes(type);
}
/**
 * The principle judge's verdict on an agent-proposed memory (stored on
 * `AgentMemory.judgeVerdict`). Kept even for a negative verdict so the reviewer
 * can see the memory WAS judged and why it didn't qualify. `principle` is the
 * judge's rewrite — "the strongest justified principle" — surfaced to the
 * reviewer next to the original content; it never silently replaces it.
 */
export const MemoryJudgeVerdictSchema = z.object({
    isPrinciple: z.boolean(),
    confidence: z.number().min(0).max(1),
    reason: z.string(),
    /** Judge's rewrite when the underlying experience supports a stronger, more general phrasing. */
    principle: z.string().nullish(),
    /**
     * Id of an EXISTING principle that already covers this lesson (judge-side
     * dedup). When set, the proposal was not promoted; the existing principle's
     * occurrence counter was bumped instead, and the reviewer sees the link.
     */
    duplicateOf: z.string().nullish(),
    /** ISO datetime the verdict was produced. */
    judgedAt: z.string(),
    /** `provider/modelId` that judged, for audit. */
    model: z.string().optional(),
});
/**
 * The reconcile pass's verdict on an agent-authored save (DEV-17252, stored on
 * `AgentMemory.reconcileVerdict`). Mem0's operation set with DELETE →
 * SUPERSEDE. `applied` records what the pass actually did — advisory verdicts
 * on queued proposals record 'recorded' and leave the reviewer the one-click
 * veto; authoritative verdicts on auto-approved rows act and record how.
 */
export const MemoryReconcileVerdictSchema = z.object({
    op: z.enum(['ADD', 'UPDATE', 'SUPERSEDE', 'NOOP']),
    /** The existing memory the op targets; null for ADD. */
    targetId: z.string().nullish(),
    reason: z.string(),
    /** Anchor set the pass extracted/backfilled (§4.4 source 3). */
    anchors: z.record(z.string(), z.string()).nullish(),
    /** Recently pruned/archived row this save matched — the wrongful-prune signal (A.1.29). */
    resurrectionOfId: z.string().nullish(),
    resurrectionSimilarity: z.number().nullish(),
    /** ISO datetime the verdict was produced. */
    judgedAt: z.string(),
    /** `provider/modelId` that judged, for audit. */
    model: z.string().optional(),
    /** Version id of the reconcile rubric that produced the verdict (`mrp1`, …). */
    promptVersion: z.string().optional(),
    /** What the pass did: skipped | recorded | deduped | updated | superseded. */
    applied: z.enum(['skipped', 'recorded', 'deduped', 'updated', 'superseded']),
});
/**
 * Retention tier (DEV-17210). `GRAPH` is the durable, decaying, graph-connected
 * tier every pre-DEV-17210 memory lives in; `WORKING` is the bounded hot set
 * (short TTL, per-agent cap, retrieval boost, no decay) that compacts into a
 * durable summary before it expires.
 */
export const MemoryTierSchema = z.enum(['WORKING', 'GRAPH']);
/**
 * Typed edge between two memories (DEV-17210). Direction is always "from → to".
 * Each type carries a traversal weight — see `memoryRelationWeight` in
 * `@cortex/database`.
 */
export const MemoryRelationTypeSchema = z.enum([
    'UPDATES',
    'CAUSED_BY',
    'RESULT_OF',
    'RELATED_TO',
    'PART_OF',
    'CONTRADICTS',
]);
/** Retrieval strategy for `searchMemories` / the search tool + API. */
export const MemorySearchModeSchema = z.enum(['hybrid', 'recent', 'important', 'typed']);
export const MemorySourceSchema = z.enum(['MANUAL', 'AGENT']);
/**
 * PENDING   — agent-authored, awaiting human approval; never injected.
 * CANDIDATE — proposed from a wrong run, sandbox-tested-with-evidence before
 *             admin review (the feedback→memory PR/release loop, DEV-16831 WS4).
 * APPROVED  — live; injected into the bound agents.
 * REJECTED  — declined during review (archived).
 */
export const MemoryStatusSchema = z.enum(['PENDING', 'CANDIDATE', 'APPROVED', 'REJECTED']);
/** 0 normal · 1 important · 2 pinned — higher priority wins the injection budget. */
export const MemoryPrioritySchema = z.number().int().min(0).max(2);
/** Max content length (also enforced at the DB + service layer). */
export const memoryContentMax = 4000;
// ─── AgentMemory (entity) ─────────────────────────────────────────────────────
export const AgentMemorySchema = z.object({
    id: z.string(),
    orgId: z.string(),
    content: z.string(),
    type: MemoryTypeSchema,
    priority: MemoryPrioritySchema,
    source: MemorySourceSchema,
    status: MemoryStatusSchema,
    /** Explicit, opt-in org-wide broadcast — reaches every agent in the org. */
    appliesToAll: z.boolean(),
    /** Copy-on-write version chain (edits cut a new version, archive the old). */
    version: z.number().int().positive(),
    supersededBy: z.string().nullable(),
    expiresAt: z.date().nullable(),
    /** Optional per-user visibility filter — orthogonal to agent binding. */
    userSubject: z.string().nullable(),
    /**
     * Memory namespace (DEV-17254, §6.5): null = production. Benchmark writes
     * always carry their run's namespace; production resolution never sees
     * namespaced rows and vice versa.
     */
    memoryNamespaceId: z.string().nullable(),
    /**
     * Optional "about-ness" filter applied WITHIN an agent's already-resolved set
     * (e.g. `{ workflowId }` so the refinement agent's guidance about workflow X
     * only surfaces when it's working on X). It never grants access — that is the
     * binding's job.
     */
    subjectRef: z.record(z.string(), z.unknown()).nullable(),
    /** The wrong Run this memory corrects (feedback→memory loop, DEV-16831). */
    sourceRunId: z.string().nullable(),
    /** The passing sandbox re-run that validated a CANDIDATE (DEV-16831 WS4). */
    evidenceRunId: z.string().nullable(),
    /**
     * How many exact-duplicate agent saves were skipped against this row (L1
     * dedup). The occurrence signal behind the reflection bar ("seen ≥3 times")
     * — a reviewer reads N+1 as "agents proposed this N+1 times in total".
     */
    duplicateSaveCount: z.number().int().nonnegative(),
    /** When an exact-duplicate save last hit this row. Null = never deduped. */
    lastDuplicateSaveAt: z.date().nullable(),
    /** Terminal soft state — memories are never hard-deleted (2026-07-10). */
    archivedAt: z.date().nullable(),
    /**
     * Approval attribution (DEV-17253, §4.8): `user:<id>` for a human review,
     * `policy:<policyId>` for auto-approval at create. Null = never approved,
     * or approved before the trail existed.
     */
    approvedBySubject: z.string().nullable(),
    approvedAt: z.date().nullable(),
    /**
     * The auto-approve policy that blessed this row — a LOOSE ref that
     * survives the policy's deletion, and the key that keeps policy-approved
     * rows judge-eligible (DEV-17256) while human-approved rows stay
     * untouchable.
     */
    approvedByPolicyId: z.string().nullable(),
    /**
     * Principle-judge verdict (null = never judged). Set asynchronously by the
     * persistent-agent host after an agent-authored save on a reflection-enabled
     * supervisor; a positive verdict also flips `type` to PRINCIPLE.
     */
    judgeVerdict: MemoryJudgeVerdictSchema.nullable(),
    /**
     * Reconcile-pass verdict (null = never reconciled). Set asynchronously by
     * the persistent-agent host after every durable agent-authored save
     * (DEV-17252, §4.6).
     */
    reconcileVerdict: MemoryReconcileVerdictSchema.nullable(),
    /** Thread turn / trace that produced an agent-authored memory. */
    sourceTraceId: z.string().nullable(),
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    // ─ Memory graph (DEV-17210) ───────────────────────────────────────────────
    /** Retention tier. `GRAPH` is the durable default; `WORKING` is the hot set. */
    tier: MemoryTierSchema,
    /**
     * Live importance in [0,1] — decayed by disuse, boosted by retrieval. What
     * the system believes right now, as opposed to `priority`, which is what a
     * human asserted and never decays.
     */
    importance: z.number().min(0).max(1),
    /** Last retrieval. Decay measures from HERE, not from `updatedAt`. */
    lastAccessedAt: z.date().nullable(),
    accessCount: z.number().int().nonnegative(),
    /** When the maintenance pass last decayed this row (idempotence stamp). */
    decayedAt: z.date().nullable(),
    /**
     * Set on the loser of a near-duplicate MERGE (distinct from `supersededBy`,
     * which is the copy-on-write edit chain), and on a working memory folded into
     * a compacted summary.
     */
    mergedInto: z.string().nullable(),
    /** Exempt from decay and pruning — set on compacted daily summaries. */
    neverPrune: z.boolean(),
});
// ─── Memory relations (the graph, DEV-17210) ─────────────────────────────────
export const AgentMemoryRelationSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    fromMemoryId: z.string(),
    toMemoryId: z.string(),
    type: MemoryRelationTypeSchema,
    /** Traversal strength in (0,2]; defaults from the type when unspecified. */
    weight: z.number().positive().max(2),
    /** The agent that asserted the edge; null when a human did. */
    sourceAgentId: z.string().nullable(),
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
});
export const CreateMemoryRelationInputSchema = z.object({
    fromMemoryId: z.string().min(1),
    toMemoryId: z.string().min(1),
    type: MemoryRelationTypeSchema,
    /**
     * Override the type's default traversal weight. Omit unless you specifically
     * want this one edge to pull harder or softer than others of its kind.
     */
    weight: z.number().positive().max(2).optional(),
});
/** One memory plus its immediate neighborhood — the console's graph view. */
export const AgentMemoryGraphSchema = z.object({
    memory: AgentMemorySchema,
    edges: z.array(AgentMemoryRelationSchema),
    /** The memories on the far end of `edges`, so the view needs one round trip. */
    neighbors: z.array(AgentMemorySchema),
});
/** A search hit: the memory, its fused score, and which sources found it. */
export const AgentMemorySearchHitSchema = AgentMemorySchema.extend({
    /** Fused RRF score (hybrid) or the retrieval weight (the simple modes). */
    searchScore: z.number(),
    /** Which retrieval sources ranked this memory: vector / fts / graph / importance. */
    searchSources: z.array(z.string()),
});
/** Provenance for a memory one agent handed to another. */
export const AgentMemoryShareSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    memoryId: z.string(),
    fromAgentId: z.string(),
    toAgentId: z.string(),
    note: z.string().nullable(),
    createdAt: z.date(),
});
// ─── Binding (memory ↔ agent, many-to-many) ──────────────────────────────────
export const AgentMemoryBindingSchema = z.object({
    memoryId: z.string(),
    agentId: z.string(),
    orgId: z.string(),
});
/**
 * A workflow LLM-node applicability target (DEV-17156). Unlike an agent/collection
 * target (a single id), a node is the STABLE compound `(workflowId, nodeKey)` —
 * NOT `WorkflowNode.id`, which is copy-on-write. This is the shape carried by
 * `nodeBindings` on the create/update contract and echoed back on the read shape,
 * so the memory-authoring surfaces can scope a memory to a specific `llm-agent`
 * node without a separate round-trip to the node-config panel.
 */
export const MemoryNodeTargetSchema = z.object({
    workflowId: z.string().min(1),
    nodeKey: z.string().min(1),
});
/**
 * API read shape — a memory plus the agent ids it is bound to. An `appliesToAll`
 * memory carries an empty `agentIds` (it reaches every agent via the flag, not a
 * binding row). `nodeBindings` lists the workflow LLM-node targets it is scoped
 * to (empty for a memory bound only to agents/collections/appliesToAll).
 */
export const AgentMemoryWithBindingsSchema = AgentMemorySchema.extend({
    agentIds: z.array(z.string()),
    collectionIds: z.array(z.string()),
    nodeBindings: z.array(MemoryNodeTargetSchema),
});
// ─── Create / Update ─────────────────────────────────────────────────────────
/**
 * Applicability IS the binding set: pass `agentIds` to bind to specific agents
 * and/or set `appliesToAll` for an org-wide memory. At least one must be given
 * so a memory is never orphaned (unreachable by any agent).
 */
export const CreateMemoryInputSchema = z
    .object({
    content: z.string().min(1).max(memoryContentMax),
    type: MemoryTypeSchema.default('CONTEXT'),
    priority: MemoryPrioritySchema.default(0),
    appliesToAll: z.boolean().default(false),
    agentIds: z.array(z.string()).default([]),
    /**
     * Bind to agent collections (DEV-16821) — the memory applies to every
     * member agent of each collection. A valid applicability target alongside
     * `agentIds`/`appliesToAll`; collections resolve to bindings at read time.
     * Optional so existing callers/SDK consumers need not pass it.
     */
    collectionIds: z.array(z.string()).optional(),
    /**
     * Bind to specific workflow LLM-agent nodes (DEV-17156) — each target is
     * the stable `(workflowId, nodeKey)`, resolved to a `WorkflowNodeMemoryBinding`
     * row at write time and injected by the runner via `resolveMemoriesForNode`.
     * A valid applicability target alongside `agentIds`/`collectionIds`/`appliesToAll`.
     * Optional so existing callers/SDK consumers need not pass it.
     */
    nodeBindings: z.array(MemoryNodeTargetSchema).optional(),
    /**
     * Per-user visibility (the user axis, DEV-16875): when set, the memory is
     * visible ONLY to this user principal — the resolver already enforces
     * `userSubject IS NULL OR userSubject = caller` at read time. Omit for a
     * memory every user sees. This is how a personal preference ("prefers
     * metric units") avoids broadcasting through a shared agent to the whole
     * org. Immutable after create: `UpdateMemoryInputSchema` deliberately has
     * no `userSubject` and copy-on-write hard-carries the old value — there is
     * no retarget path and no clear-to-shared path. The API additionally
     * rejects a create targeting a DIFFERENT user unless the caller is a
     * memory governor (memory.controller.ts).
     */
    userSubject: z.string().min(1).optional(),
    subjectRef: z.record(z.string(), z.unknown()).optional(),
    expiresAt: z.string().datetime().optional(),
    /** Set by the feedback→memory loop when a memory is proposed from a run. */
    sourceRunId: z.string().optional(),
    /**
     * Retention tier (DEV-17210). Defaults to `GRAPH` — the durable, reviewed,
     * decaying tier. `WORKING` is the bounded hot set: fixed short TTL, hard
     * per-agent cap, retrieval boost, compacted into a permanent summary before
     * it expires. Manual API writes are rarely working-tier; the tool path is.
     */
    tier: MemoryTierSchema.optional(),
})
    .superRefine((v, ctx) => {
    if (!v.appliesToAll &&
        v.agentIds.length === 0 &&
        (v.collectionIds?.length ?? 0) === 0 &&
        (v.nodeBindings?.length ?? 0) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A memory must bind to at least one agent, collection, or workflow node, or set appliesToAll',
            path: ['agentIds'],
        });
    }
});
export const UpdateMemoryInputSchema = z.object({
    content: z.string().min(1).max(memoryContentMax).optional(),
    type: MemoryTypeSchema.optional(),
    priority: MemoryPrioritySchema.optional(),
    appliesToAll: z.boolean().optional(),
    agentIds: z.array(z.string()).optional(),
    /** Rebind the memory's collections (DEV-16821); omit to leave unchanged. */
    collectionIds: z.array(z.string()).optional(),
    /**
     * Rebind the memory's workflow LLM-node targets (DEV-17156); omit to carry
     * the existing set forward across the copy-on-write version cut. Passing an
     * explicit array (including `[]` to clear) replaces the set.
     */
    nodeBindings: z.array(MemoryNodeTargetSchema).optional(),
    subjectRef: z.record(z.string(), z.unknown()).nullable().optional(),
    expiresAt: z.string().datetime().nullable().optional(),
});
// ─── Metrics ──────────────────────────────────────────────────────────────────
/** Bounds for the metrics window; clamped at the service. */
/** Batch governance (DEV-16845): approve/reject up to 100 candidates at once. */
export const BatchMemoryActionInputSchema = z.object({
    ids: z.array(z.string()).min(1).max(100),
});
export const BatchApproveMemoriesResultSchema = z.object({
    approved: z.number().int(),
    skipped: z.array(z.string()),
    errors: z.array(z.string()),
});
export const BatchRejectMemoriesResultSchema = z.object({
    rejected: z.number().int(),
    skipped: z.array(z.string()),
    errors: z.array(z.string()),
});
/** Attach the passing re-run that validated a CANDIDATE (DEV-16842). */
export const AttachMemoryEvidenceInputSchema = z.object({
    runId: z.string().min(1),
});
export const memoryMetricsMinDays = 7;
export const memoryMetricsMaxDays = 365;
export const memoryMetricsDefaultDays = 30;
/** Cap for the recently-archived list on the metrics dashboard. */
export const memoryMetricsRecentlyArchivedLimit = 10;
/**
 * Corpus health snapshot for the console memory dashboard (DEV-16963). Adapted
 * from the theopolis refinement-memory metrics to the unified model: source is
 * the MANUAL/AGENT enum (not a free string), scope reflects the binding model
 * (appliesToAll / agent-bound / subject-scoped) rather than org/workflow/agent,
 * and the queue counts include the CANDIDATE state the unified store added.
 *
 * Live-only semantics (matches theopolis): byStatus, sourceSplit,
 * scopeDistribution, pendingQueue, and byAgent ignore archived / CoW history
 * rows (`archivedAt IS NULL`). `archived` + `total` span the whole corpus.
 * Windowed fields (approvalRate, savesPerWeek) use the clamped `days` window;
 * recentlyArchived is the latest N archived rows org-wide (not windowed).
 */
export const AgentMemoryMetricsSchema = z.object({
    /** The window applied to rate/trend fields, after clamping (7–365, default 30). */
    windowDays: z.number().int().positive(),
    /**
     * Live (non-archived) counts by lifecycle state. Reject is auto-archived in
     * the unified store, so `rejected` is typically 0 here — see `archived`.
     */
    byStatus: z.object({
        pending: z.number().int().nonnegative(),
        candidate: z.number().int().nonnegative(),
        approved: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
    }),
    /** Rows with archivedAt set (rejected + CoW supersessions + manual archives). */
    archived: z.number().int().nonnegative(),
    /** All rows in the org (live + archived). */
    total: z.number().int().nonnegative(),
    /** Author split across the live corpus. */
    sourceSplit: z.object({
        manual: z.number().int().nonnegative(),
        agent: z.number().int().nonnegative(),
    }),
    /**
     * Reach of live (non-archived) memories. Non-exclusive: an appliesToAll memory
     * may also be subject-scoped, so these need not sum to the live total.
     */
    scopeDistribution: z.object({
        appliesToAll: z.number().int().nonnegative(),
        agentBound: z.number().int().nonnegative(),
        subjectScoped: z.number().int().nonnegative(),
        /** Live memories visible to a single user only (userSubject set). */
        userScoped: z.number().int().nonnegative(),
    }),
    /**
     * Of proposals CREATED in the window (live rows + REJECTED decisions; CoW
     * history excluded): approved / (approved + rejected + pending + candidate).
     * Null when the denominator is 0.
     */
    approvalRate: z.object({
        windowDays: z.number().int().positive(),
        approved: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
        candidate: z.number().int().nonnegative(),
        rate: z.number().min(0).max(1).nullable(),
    }),
    /**
     * L1 exact-dedup pressure — the pilot's duplicate-rate dial (the metric the
     * v3 gates read). Live-only, like the other corpus counts. `skipsInWindow`
     * counts memories whose LAST duplicate hit fell in the window (there is no
     * per-skip event table), so it under-counts bursts; totals are exact.
     */
    exactDuplicates: z.object({
        /** Sum of duplicateSaveCount across live memories — total skipped saves. */
        totalSkips: z.number().int().nonnegative(),
        /** Live memories with at least one skipped duplicate save. */
        memoriesAffected: z.number().int().nonnegative(),
        /** Live memories whose lastDuplicateSaveAt falls inside the window. */
        skipsInWindow: z.number().int().nonnegative(),
    }),
    /** Current review backlog (live PENDING + CANDIDATE), not windowed. */
    pendingQueue: z.object({
        count: z.number().int().nonnegative(),
        /** ISO datetime of the oldest item still awaiting review. Null when empty. */
        oldestCreatedAt: z.string().nullable(),
        /** Age of that oldest item in whole days. Null when empty. */
        oldestAgeDays: z.number().int().nonnegative().nullable(),
        /** Escalation signal — backlog items older than 7 days. */
        olderThan7dCount: z.number().int().nonnegative(),
    }),
    /** Most recently archived memories (org-wide, newest first; not windowed). */
    recentlyArchived: z.array(z.object({
        id: z.string(),
        content: z.string(),
        type: MemoryTypeSchema,
        archivedAt: z.string(),
    })),
    /**
     * Weekly created-count series over the window, oldest first, gap-densified
     * (ISO weeks, Monday 00:00 UTC). weekStart is an ISO datetime. Includes
     * archived rows — a save is a save regardless of later fate.
     */
    savesPerWeek: z.array(z.object({
        weekStart: z.string(),
        count: z.number().int().nonnegative(),
    })),
    /**
     * Top bound agents by live pending(+candidate) then approved, descending.
     * CANDIDATE folds into `pending` (same review backlog as pendingQueue).
     */
    byAgent: z.array(z.object({
        agentId: z.string(),
        pending: z.number().int().nonnegative(),
        approved: z.number().int().nonnegative(),
    })),
});
//# sourceMappingURL=agent-memory.js.map